import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import MultiviewMock from "./mocks/MultiviewMock";
import FleetMock from "./mocks/FleetMock";
import AnalysisMock from "./mocks/AnalysisMock";
import HardwareGrid from "./mocks/HardwareGrid";

interface Row {
  key: "live" | "fleet" | "analysis" | "hardware";
  accent: "violet" | "blue";
  metaCount: number;
  status?: boolean;
  visual: ReactNode;
}

const ROWS: Row[] = [
  { key: "live", accent: "violet", metaCount: 3, visual: <MultiviewMock /> },
  { key: "fleet", accent: "blue", metaCount: 3, visual: <FleetMock /> },
  {
    key: "analysis",
    accent: "violet",
    metaCount: 3,
    status: true,
    visual: <AnalysisMock />,
  },
  { key: "hardware", accent: "blue", metaCount: 0, visual: <HardwareGrid /> },
];

export default function HomeFeatureRows() {
  const { t } = useTranslation("pages");

  return (
    <section className="bg-gray-950 text-white">
      <div className="mx-auto max-w-6xl px-6 pb-16 lg:pb-20">
        {ROWS.map((row) => (
          <article
            key={row.key}
            className="grid items-center gap-6 border-t border-gray-800 py-12 lg:grid-cols-[120px_1fr_1.2fr] lg:gap-10 lg:py-14"
          >
            <p
              className={clsx(
                "flex items-center gap-2 self-start font-mono text-[11px] uppercase tracking-[0.14em] lg:pt-2",
                row.accent === "violet" ? "text-violet-400" : "text-primary-400",
              )}
            >
              {t(`home.rows.${row.key}.label`)}
              {row.status && (
                <span
                  data-testid={`home-${row.key}-status`}
                  className="rounded border border-gray-700 px-1.5 py-px text-[9px] tracking-[0.12em] text-gray-400"
                >
                  {t(`home.rows.${row.key}.status`)}
                </span>
              )}
            </p>
            <div>
              <h3 className="mb-3 text-2xl font-bold tracking-tight text-balance sm:text-[2rem] sm:leading-tight">
                {t(`home.rows.${row.key}.title`)}
              </h3>
              <p className="max-w-[46ch] text-gray-400">
                {t(`home.rows.${row.key}.body`)}
              </p>
              {row.metaCount > 0 && (
                <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11.5px] text-gray-400">
                  {Array.from({ length: row.metaCount }, (_, i) => i + 1).map(
                    (n) => (
                      <li key={n}>
                        <b className="font-medium text-white">
                          {t(`home.rows.${row.key}.meta${n}Bold`)}
                        </b>{" "}
                        {t(`home.rows.${row.key}.meta${n}`)}
                      </li>
                    ),
                  )}
                </ul>
              )}
            </div>
            <div className="min-w-0">{row.visual}</div>
          </article>
        ))}
      </div>
    </section>
  );
}
